// Moved from `src/hooks/mutation/useMutateUpdateClinicalTask.ts` (now a
// delegate) with the D2 family (ARCFE-945). Cohort B: the success analytics
// branched on the optimistic snapshot's `previousTask` (stateful edit event vs
// plain edit event) — `onUpdateSuccess` carries it so the delegate replays the
// branch; the error toast crosses `onUpdateError` after rollback (ARCFE-921).
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { logger } from '@heidi/runtime/logger';
import type { ClinicalTaskCategory, ClinicalTaskStatus } from '@heidi/schema/ml-scribe';

import { withClinicalTaskStatusTransition } from '../../contracts/status';
import type {
  ClinicalTask,
  ClinicalTaskResponse,
  GetClinicalTasksResponse,
} from '../../contracts/types';
import { updateClinicalTask } from '../api/clinical-tasks';
import {
  invalidateClinicalTaskQueries,
  snapshotClinicalTaskQueries,
} from './clinicalTaskQueryCache';

export interface UpdateClinicalTaskParams {
  taskId: string;
  taskData: {
    title?: string;
    description?: string;
    due_date?: string | null;
    category?: ClinicalTaskCategory;
    status?: ClinicalTaskStatus;
    source_quote?: string | null;
    is_conditional?: boolean;
  };
}

interface MutationContext {
  previousTask?: ClinicalTask;
  previousSnapshots: [QueryKey, GetClinicalTasksResponse | undefined][];
}

interface UseMutateUpdateClinicalTaskProps {
  /** App-reaction port: fired where the legacy toast fired (ARCFE-921). */
  onUpdateError?: (error: AxiosError) => void;
  /** App-reaction port: fired where the legacy analytics dispatch fired. */
  onUpdateSuccess?: (
    data: ClinicalTaskResponse,
    previousTask: ClinicalTask | undefined
  ) => void;
}

export const useMutateUpdateClinicalTask = (props?: UseMutateUpdateClinicalTaskProps) => {
  const queryClient = useQueryClient();

  return useMutation<
    ClinicalTaskResponse,
    AxiosError,
    UpdateClinicalTaskParams,
    MutationContext
  >({
    mutationFn: async ({ taskId, taskData }) => {
      const { data } = await updateClinicalTask(taskId, taskData);
      return data;
    },
    onMutate: ({ taskId, taskData }): MutationContext => {
      // basically the tasks can exist in multiple places in the cache
      let previousTask: ClinicalTask | undefined = undefined;
      const previousSnapshots = snapshotClinicalTaskQueries(queryClient);

      const applyUpdate = (old: GetClinicalTasksResponse | undefined) => {
        if (!old?.tasks) return old;
        return {
          ...old,
          tasks: old.tasks.map((task) => {
            if (task.id != taskId) return task;
            const updatedTask = { ...task, ...taskData };
            return taskData.status === undefined
              ? updatedTask
              : withClinicalTaskStatusTransition(
                  updatedTask,
                  taskData.status,
                  task.status
                );
          }),
        };
      };

      for (const [queryKey, data] of previousSnapshots) {
        const task = data?.tasks?.find((task) => task.id == taskId);
        // First match wins. Every surface holds the same task, so pin one copy
        // for the edit analytics instead of letting key order pick the winner.
        // Not a `break`: this loop also applies the optimistic update below.
        previousTask ??= task;
        // this is an artifact of having the params being used as the query key
        // wouldnt be an issue, but we have no way to know what they are
        // FIXME: use atoms for the query params
        queryClient.setQueryData<GetClinicalTasksResponse>(queryKey, applyUpdate);
      }

      return { previousTask, previousSnapshots };
    },
    onSettled: () => invalidateClinicalTaskQueries(queryClient),
    onError: (error, _variables, context) => {
      if (context?.previousSnapshots) {
        for (const [key, data] of context.previousSnapshots) {
          queryClient.setQueryData(key, data);
        }
      }

      logger.error('Error updating clinical task:', error.response?.data);
      props?.onUpdateError?.(error);
    },
    onSuccess: (data, _, context) => {
      props?.onUpdateSuccess?.(data, context?.previousTask);
    },
  });
};
